import { useState, useEffect, memo } from 'react';
import SuccessModal from './components/SuccessModal';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { settings } from './Settings';
import { Header } from './components/Header';
import { Autocomplete, Box } from '@mui/material';
import TextfieldNoborder from './components/TextfieldNoborder';


const apiURL = settings.baseUrl;
const imageUriPath = settings.imageUriPath;
const db = settings.MongodbAppName;
const collection = 'batch';

// Mobile-optimized Grid component (module scope, memoized)
const MobileGrid = memo(({ item, container, xs, sm, md, spacing, className, children, ...props }) => {
  let gridClasses = 'flex flex-col';
  
  if (container) {
    gridClasses = 'flex flex-wrap -mx-2';
  }
  
  if (item) {
    let sizeClasses = 'px-2 mb-4';
    
    if (xs === 12) sizeClasses += ' w-full';
    else if (xs === 6) sizeClasses += ' w-full sm:w-1/2';
    else if (xs === 4) sizeClasses += ' w-full sm:w-1/2 md:w-1/3';
    else if (xs === 3) sizeClasses += ' w-full sm:w-1/2 md:w-1/4';
    else if (xs === 0) sizeClasses += ' hidden md:block md:w-0';
    
    if (sm === 6) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-1/2');
    if (sm === 4.5) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-5/12');
    if (sm === 4) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-1/3');
    if (sm === 3) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-1/4');
    if (sm === 8) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-2/3');
    
    if (md === 4) sizeClasses += ' md:w-1/3';
    if (md === 3) sizeClasses += ' md:w-1/4';
    if (md === 2) sizeClasses += ' md:w-1/6';
    if (md === 8) sizeClasses += ' md:w-2/3';
    
    gridClasses = sizeClasses;
  }
  
  return (
    <div className={`${gridClasses} ${className || ''}`} {...props}>
      {children}
    </div>
  );
});

// Mobile-optimized Autocomplete component (module scope, memoized)
const MobileAutocomplete = memo(({ 
  options, 
  value, 
  onChange, 
  getOptionLabel, 
  disabled,
  // filter out props that belong to MUI Autocomplete so they are not passed to native select
  fullWidth,
  autoHighlight,
  className,
}) => (
  <select
    value={value?._id || ''}
    onChange={(e) => {
      const selectedOption = options.find(opt => opt._id === e.target.value);
      onChange(e, selectedOption);
    }}
    disabled={disabled}
    className={`${className || ''} w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1`}
    required
  >
    <option value="">Select Teacher</option>
    {options.map((option, index) => (
      <option key={index} value={option._id}>
        {getOptionLabel(option)}
      </option>
    ))}
  </select>
));

const AddBatchMobile = () => {
  return <BatchForm />;
};

function BatchForm() {
  const navigate = useNavigate();
  const { id, type } = useParams();

  const [batch, setbatch] = useState({
    title: '',
    description: '',
    repeat: 'Weekly',
    repeat_on: [],
    timeAt: 'Am',
    repeat_after: 1,
    after_occurance: '',
    end_date_type: '2',
    approval_status: 'pending',
    recurring: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [centers, setCenters] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teachersSorted, setTeachersSorted] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isImageError, setIsImageError] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchCourses();
    fetchTeachers();
    fetchLanguages();
    fetchCenters();
  }, [id, type]);

  // Native fetch replacement for axios (keeping same functionality)
  const fetchWithConfig = async (url, options = {}) => {
    const response = await fetch(`${apiURL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  };

  const validateForm = () => {
    const errors = {};

    // Image validation
    if (!imageFile && !batch?.imagePath) {
      errors.image = 'Batch banner is required';
      setIsImageError(true);
    }

    // Course validation
    if (!batch.course?._id) {
      errors.course = 'Please select a course';
    }

    // Teacher validation
    if (!batch.teacher?._id) {
      errors.teacher = 'Please select a teacher';
    }

    // Language validation
    if (!batch.language) {
      errors.language = 'Please select a language';
    }

    // Center validation
    if (!batch.center) {
      errors.center = 'Please select a center';
    }

    // Mode of Teaching validation
    if (!batch.location) {
      errors.location = 'Please select mode of teaching';
    }

    // Conditional validations based on location
    if ((batch.location === 'Physical' || batch.location === 'Online & Physical') && !batch.address) {
      errors.address = 'Address is required for physical classes';
    }

    // Registration Link validation
    if (!batch.registrationLink) {
      errors.registrationLink = 'Registration link is required';
    }

    // Date validation
    if (!batch.date) {
      errors.date = 'Please select a date';
    }

    // Time validation
    if (!batch.time) {
      errors.time = 'Please select a time';
    }

    // Hours validation
    if (!batch.hours && batch.hours !== 0) {
      errors.hours = 'Please select hours';
    }

    // Minutes validation
    if (!batch.minutes && batch.minutes !== 0) {
      errors.minutes = 'Please select minutes';
    }

    // Recurring validation
    if (batch.recurring) {
      if (!batch.end_date_type) {
        errors.end_date_type = 'Please select an end date option';
      }

      if (batch.end_date_type === '1' && !batch.by_date) {
        errors.by_date = 'Please select an end date';
      }
      // If "After" is selected, occurrences are required
      if (batch.end_date_type === '2' && (!batch.after_occurance || batch.after_occurance === '')) {
        errors.after_occurance = 'Please select total occurrences';
      }

    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchCenters = async () => {
    try {
      const response = await fetchWithConfig(`/read?db=${db}&collection=centers`);
      setCenters(response);
    } catch (error) {
      setError(true);
    }
  };

  const fetchCourses = async (batch) => {
    try {
      const collection = 'courses';
      const filter = { status: 'true' };
      const sort = { createdAt: 1 };
      const queryParams = new URLSearchParams({
        db,
        collection,
        filter: JSON.stringify(filter),
        sort: JSON.stringify(sort),
      });
      const response = await fetchWithConfig(`/read?${queryParams.toString()}`);
      const fetchedCourses = response;
      setCourses(fetchedCourses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError(true);
    }
  };

useEffect(() => {
  if (batch?.course?._id) {
    hasCourse(batch.course._id);
  }
}, [batch?.course?._id]);

  const hasCourse = async (value) => {
    try {
      setbatch((prevSchema) => {
        const updatedSchema = { ...prevSchema };
        courses.map((item, index) => {
          if (item._id === value) {
            updatedSchema.course = item;
          }
        });
        setTeachersSorted([]);
        const teachersCopy = [...teachers];

        const filteredTeachers =
          teachersCopy &&
          teachersCopy.filter(
            (teacher) =>
              teacher.courses &&
              Object.values(teacher.courses).some(
                (course) => course._id === value
              )
          );

        setTeachersSorted(
          filteredTeachers.sort((a, b) => a.name.localeCompare(b.name))
        );
        return updatedSchema;
      });
    } catch (error) {
      console.error('Error in hasCourse:', error);
      setError(true);
    }
  };

const hasCenter = async (e) => { 
  const value = e.target.value;
  setbatch((prevBatch) => {
    const updatedBatch = { ...prevBatch };
    const teacher = teachers.find((t) => t._id === value);

    if (teacher) {
      updatedBatch.teacher = teacher;
      updatedBatch.whatsapp = teacher.whatsapp;
      updatedBatch.youtube = teacher.youtube;

      const centerFromTeacher = teacher.center?.trim() || "";
      // Optional: match with centers array to ensure select works
      const matchedCenter = centers.find(
        (cs) => cs.title.trim().toLowerCase() === centerFromTeacher.toLowerCase()
      );
      updatedBatch.center = matchedCenter ? matchedCenter.title : ""; 
    }
    return updatedBatch;
  });
};


  const fetchTeachers = async (batch) => {
    try {
      const response = await fetchWithConfig(`/read?db=${db}&collection=teachers`);
      const fetchedTeachers = response;
      setTeachers(fetchedTeachers);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError(true);
    }
  };

  const fetchLanguages = async (batch) => {
    try {
      const response = await fetchWithConfig(`/read?db=${db}&collection=languages`);
      const fetchedCourses = response;
      setLanguages(fetchedCourses);
    } catch (error) {
      setError(true);
    }
  };

  const setBox = (e) => {
    setbatch((prevSchema) => {
      const updatedSchema = { ...prevSchema };
      updatedSchema.recurring = e.target.checked;
      return updatedSchema;
    });
  };

  const updateCourse = (key, value, current, ind) => {
    const updatedbatch = { ...batch };
    const updatedCourse = { ...courses };
    if (!current) {
      updatedbatch.courses[value._id] = { _id: value._id, title: value.title };
      updatedCourse[ind].state = true;
    } else {
      delete updatedbatch.courses[value._id];
      updatedCourse[ind].state = false;
    }
    setbatch(updatedbatch);
  };

  const handleChangeRpeatOn = (value) => {
    var temp = [...batch.repeat_on];
    if (temp.indexOf(value) === -1) {
      temp.push(value);
    } else {
      temp = temp.filter((item) => value != item);
    }
    setbatch({ ...batch, repeat_on: temp });
  };

  const isRpeatOnMatch = (value) => {
    var temp = [...batch.repeat_on];
    if (temp.indexOf(value) === -1) {
      return false;
    }
    return true;
  };

  // Native date formatting replacement for moment.js
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

const addNew = async (e) => {
  e.preventDefault();

  if (loading) return;
  // Client-side validation
  if (!validateForm()) {
    return;
  }
  // Mobile-specific validation
  // if (!validateFormMobile()) {
  //   return;
  // }
  if (!imageFile && !batch?.imagePath) {
    setIsImageError(true);
    return;
  }

  setLoading(true);

  try {
    const formData = new FormData();
    formData.append('db', db);
    formData.append('collection', collection);
    formData.append('data', JSON.stringify(batch));
    if (imageFile) formData.append('image', imageFile);

    const url = `${apiURL}/insert`;
    const response = await fetch(url, { method: 'POST', body: formData });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    // Showing success modal after upload completes
    setShowSuccessModal(true);
    setLoading(false);
    // Auto closing modal and redirect after 2 seconds
    setTimeout(() => {
      setShowSuccessModal(false);
      navigate(-1);
    }, 2000);
  } catch (error) {
    console.error('Add batch error:', error);
    setError(true);
    setLoading(false);
  }
};

  const handleUploadImage = (e) => {
    if (e?.target?.files && e?.target?.files.length > 0) {
      const file = e.target.files[0];
      setIsImageError(false);
      setImageFile(file);
      setImageUrl(URL.createObjectURL(file));
    }
  };

  // Mobile-optimized Grid component
  // const MobileGrid = ({ item, container, xs, sm, md, spacing, className, children, ...props }) => {
  //   let gridClasses = 'flex flex-col';
    
  //   if (container) {
  //     gridClasses = 'flex flex-wrap -mx-2';
  //   }
    
  //   if (item) {
  //     let sizeClasses = 'px-2 mb-4';
      
  //     if (xs === 12) sizeClasses += ' w-full';
  //     else if (xs === 6) sizeClasses += ' w-full sm:w-1/2';
  //     else if (xs === 4) sizeClasses += ' w-full sm:w-1/2 md:w-1/3';
  //     else if (xs === 3) sizeClasses += ' w-full sm:w-1/2 md:w-1/4';
  //     else if (xs === 0) sizeClasses += ' hidden md:block md:w-0';
      
  //     if (sm === 6) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-1/2');
  //     if (sm === 4.5) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-5/12');
  //     if (sm === 4) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-1/3');
  //     if (sm === 3) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-1/4');
  //     if (sm === 8) sizeClasses = sizeClasses.replace('sm:w-1/2', 'sm:w-2/3');
      
  //     if (md === 4) sizeClasses += ' md:w-1/3';
  //     if (md === 3) sizeClasses += ' md:w-1/4';
  //     if (md === 2) sizeClasses += ' md:w-1/6';
  //     if (md === 8) sizeClasses += ' md:w-2/3';
      
  //     gridClasses = sizeClasses;
  //   }
    
  //   return (
  //     <div className={`${gridClasses} ${className || ''}`} {...props}>
  //       {children}
  //     </div>
  //   );
  // };

  // Mobile-optimized Autocomplete component
  // const MobileAutocomplete = ({ 
  //   options, 
  //   value, 
  //   onChange, 
  //   getOptionLabel, 
  //   disabled,
  //   ...props 
  // }) => (
  //   <select
  //     value={value?._id || ''}
  //     onChange={(e) => {
  //       const selectedOption = options.find(opt => opt._id === e.target.value);
  //       onChange(e, selectedOption);
  //     }}
  //     disabled={disabled}
  //     className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
  //     required
  //   >
  //     <option value="">Select Teacher</option>
  //     {options.map((option, index) => (
  //       <option key={index} value={option._id}>
  //         {getOptionLabel(option)}
  //       </option>
  //     ))}
  //   </select>
  // );

  const clearError = (field) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  return (
    <div className="container-fluid">
      <Header />
      <SuccessModal
        open={showSuccessModal}
        title={id && id !== 'new' ? 'Success!' : 'Success!'}
        message={id && id !== 'new' ? 'Batch has been updated successfully.' : 'Batch has been created successfully.'}
      />
      <div>
        <div className="container z-10 relative mt-[-500px] sm:mt-[-300px] sm:px-[40px] sm:py-[40px] px-2 py-6 mt-10 tds">
          <div className="">
            <div className="grid grid-cols-12 gap-y-10 gap-x-6">
              <div className="col-span-12">
                <div className="flex flex-col gap-y-3 items-center mb-10">
                  <div className="text-base text-[22px] sm:text-[35px] mb-2 group-[.mode--light]:text-white flex justify-center font-bold items-center">
                    Batch Registration
                  </div>
                  <Link
                    to="/"
                    className="m-4 px-6 py-2 bg-[#2271B1] self-start rounded-full text-white"
                      >Back
                  </Link>
                  <p className="text-sm group-[.mode--light]:text-white flex justify-center font-bold items-center">
                    Enter your course details to register your batch.
                  </p>
                </div>

                <div className="container z-10 relative">
                  <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                      <div className="mt-3.5 grid grid-cols-12 xl:grid-cols-12 gap-y-7 lg:gap-y-10 gap-x-6">
                        <div className="relative flex flex-col col-span-12 lg:col-span-12 xl:col-span-12 gap-y-7">
                          <div className="flex flex-col p-5 box box--stacked bg-white rounded-lg shadow">
                            <div>
                              <form onSubmit={addNew}>
                                <div className="border rounded-[0.6rem] dark:border-darkmode-400 relative mt-7 mb-4 border-slate-200/80">
                                  <div className="absolute left-0 px-3 ml-4 -mt-2 text-xs uppercase bg-white text-slate-500">
                                    <div className="-mt-px">batch</div>
                                  </div>
                                  <div className="px-2 sm:px-5 py-2 mt-4 flex flex-col gap-3.5">
                                    <MobileGrid
                                      container
                                      spacing={3}
                                      className="px-2 sm:px-5"
                                    >
                                      <MobileGrid item xs={12}>
                                        <div className="mt-6">
                                          <input
                                            type="file"
                                            accept="image/png, image/gif, image/jpeg"
                                            onChange={handleUploadImage}
                                            id="upload-image"
                                            className="hidden"
                                          />
                                          <label
                                            htmlFor="upload-image"
                                            className={`w-full bg-gray-100 ${isImageError || validationErrors.image ? 'border-2 border-red-500' : ''}`}
                                          >
                                            <div className="max-h-[800px] min-h-64 bg-inherit mt-6 w-full flex flex-col items-center justify-center">
                                              {imageUrl && (
                                                <div className="flex flex-col items-center justify-center w-full">
                                                  <img
                                                    src={imageUrl}
                                                    alt="teacher-image"
                                                    className="max-h-[800px] w-full object-cover"
                                                  />
                                                </div>
                                              )}
                                              {!imageUrl && (
                                                <div className="flex flex-col items-center justify-center gap-1 w-full">
                                                  <h3 className="text-lg font-bold">
                                                    Batch Banner
                                                  </h3>
                                                  <div className="text-sm">
                                                    Please select a file to
                                                    add/update
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </label>
                                        </div>
                                        {(isImageError || validationErrors.image) && (
                                          <div className="bg-red-100 text-red-700 px-4 py-2 my-1 rounded">
                                            {validationErrors.image || 'Batch banner is required.'}
                                          </div>
                                        )}
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="Course-Name"
                                              className="inline-block mb-2"
                                            >
                                              Course Name<span className="text-red-500">*</span>
                                            </label>
                                          </div>
                                          {courses && courses.length && (
                                          <>
                                            <div className={validationErrors.course ? 'border-2 border-red-500 rounded' : ''}>
                                            <select
                                              onChange={(e) =>
                                                hasCourse(e?.target?.value)
                                              }
                                              value={batch.course?._id || ''}
                                              // required
                                              className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                            >
                                              <option value="">
                                                Select Course
                                              </option>
                                              {courses.map((cs, index) => (
                                                <option
                                                  key={index}
                                                  value={cs._id}
                                                >
                                                  {cs.title}
                                                </option>
                                              ))}
                                            </select>
                                            </div>
                                            {validationErrors.course && (
                                                <div className="text-red-500 text-sm mt-1">
                                                  {validationErrors.course}
                                                </div>
                                              )}
                                          </>
                                          )}
                                        </div>
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <>
                                            <div className="mt-4">
                                              <label
                                                htmlFor="TeacherName"
                                                className="inline-block mb-2"
                                              >
                                                Teacher Name<span className="text-red-500">*</span>
                                              </label>
                                            </div>
                                            <div className={validationErrors.teacher ? 'border-2 border-red-500 rounded' : ''}>
                                            <Autocomplete
                                              fullWidth
                                              size="small"
                                              id="teacher-select"
                                              onChange={(e, value) => {
                                                hasCenter({
                                                  target: {
                                                    value: value?._id || '',
                                                  },
                                                });
                                                clearError('teacher');
                                              }}
                                              disabled={
                                                !batch.course &&
                                                !teachersSorted.length
                                              }
                                              value={
                                                teachersSorted.find(
                                                  (item) =>
                                                    item._id ===
                                                    batch.teacher?._id
                                                ) || null
                                              }
                                              options={teachersSorted}
                                              autoHighlight
                                              getOptionLabel={(option) =>
                                                option.name
                                              }
                                              renderOption={(props, option) => {
                                                const { key, ...optionProps } = props;
                                                return (
                                                  <Box key={key} component="li" {...optionProps}>
                                                    {option.name}
                                                  </Box>
                                                );
                                              }}
                                              renderInput={(params) => (
                                                <TextfieldNoborder
                                                  {...params}
                                                  inputProps={{
                                                    ...params.inputProps,
                                                    autoComplete: 'new-password',
                                                  }}
                                                />
                                              )}
                                            />
                                            </div>
                                            {validationErrors.teacher && (
                                                <div className="text-red-500 text-sm mt-1">
                                                  {validationErrors.teacher}
                                                </div>
                                              )}
                                          </>
                                        </div>
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="Language"
                                              className="inline-block mb-2"
                                            >
                                              Language<span className="text-red-500">*</span>
                                            </label>
                                          </div>
                                          {languages && languages.length && (
                                             <>
                                              <div className={validationErrors.language ? 'border-2 border-red-500 rounded' : ''}>
                                            <select
                                              value={batch.language || ''}
                                              onChange={(e) =>
                                                setbatch({
                                                  ...batch,
                                                  language: e.target.value,
                                                })
                                              }
                                              // required
                                              className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                            >
                                              <option value="">
                                                Select Language
                                              </option>
                                              {languages.map((cs, index) => (
                                                <option key={index} value={cs.title}>
                                                  {cs.title}
                                                </option>
                                              ))}
                                            </select>
                                             </div>
                                              {validationErrors.language && (
                                                <div className="text-red-500 text-sm mt-1">
                                                  {validationErrors.language}
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="Center"
                                              className="inline-block mb-2"
                                            >
                                              Center<span className="text-red-500">*</span>
                                            </label>
                                          </div>

                                          {centers && centers.length && (
                                             <>
                                              <div className={validationErrors.center ? 'border-2 border-red-500 rounded' : ''}>
                                            <select
                                              value={batch.center ? centers.find(c => c.title.toLowerCase() === batch.center.toLowerCase())?.title : ''}
                                              onChange={(e) =>
                                                setbatch({
                                                  ...batch,
                                                  center: e.target.value,
                                                })
                                              }
                                              // required
                                              className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                            >
                                              <option value="">
                                                Select Center
                                              </option>
                                              {centers.map((cs, index) => (
                                                <option key={index} value={cs.title}>
                                                  {cs.title}
                                                </option>
                                              ))}
                                            </select>
                                            </div>
                                              {validationErrors.center && (
                                                <div className="text-red-500 text-sm mt-1">
                                                  {validationErrors.center}
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="Assistant-Teacher" className="inline-block mb-2">
                                              Assistant Teacher
                                            </label>
                                          </div>
                                          <input
                                            id="Assistant-Teacher"
                                            type="text"
                                            placeholder="Input text"
                                            value={batch.assistant || ''}
                                            onChange={(e) =>
                                              setbatch({
                                                ...batch,
                                                assistant: e.target.value,
                                              })
                                            }
                                            className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                          />
                                        </div>
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="Mode-Teaching"
                                              className="inline-block mb-2"
                                            >
                                              Mode of Teaching<span className="text-red-500">*</span>
                                            </label>
                                          </div>
                                          <div className={validationErrors.location ? 'border-2 border-red-500 rounded' : ''}>
                                         <select
                                              id="location-select"
                                              value={batch.location || ''}
                                              onChange={(e) => {
                                                setbatch({
                                                  ...batch,
                                                  location: e.target.value,
                                                });
                                                clearError('location');
                                              }}
                                              className="w-full rounded p-2.5 border-0"
                                              style={{ outline: 'none' }}
                                            >
                                            <option value="">Mode of Teaching</option>
                                            <option value="Online">Online</option>
                                            <option value="Physical">Physical</option>
                                            <option value="Online & Physical">Online & Physical</option>
                                          </select>
                                        </div>
                                          {validationErrors.location && (
                                            <div className="text-red-500 text-sm mt-1">
                                              {validationErrors.location}
                                            </div>
                                          )}

                                        </div>
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="WhatsApp"
                                              className="inline-block mb-2"
                                            >
                                              WhatsApp
                                            </label>
                                          </div>
                                          <input
                                            id="WhatsApp"
                                            type="text"
                                            placeholder="Input text"
                                            value={batch.whatsapp || ''}
                                            onChange={(e) =>
                                              setbatch( {
                                                ...batch,
                                                whatsapp: e.target.value,
                                              })
                                            }
                                            className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                          />
                                        </div>
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="Youtube"
                                              className="inline-block mb-2"
                                            >
                                              Youtube
                                            </label>
                                          </div>
                                          <input
                                            id="Youtube"
                                            type="text"
                                            placeholder="Input text"
                                            value={batch.youtube || ''}
                                            onChange={(e) =>
                                              setbatch({
                                                ...batch,
                                                youtube: e.target.value,
                                              })
                                            }
                                            className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                          />
                                        </div>
                                      </MobileGrid>
                                      {(batch.location === 'Online' ||
                                        batch.location ===
                                          'Online & Physical') && (
                                        <MobileGrid item md={4} sm={6} xs={12}>
                                          <div>
                                            <div className="mt-4">
                                              <label
                                                htmlFor="Meeting-Link"
                                                className="inline-block mb-2"
                                              >
                                                Meeting Link
                                              </label>
                                            </div>
                                            <input
                                              id="MeetingLink"
                                              type="text"
                                              placeholder="Input text"
                                              value={batch.meetingLink || ''}
                                              onChange={(e) =>
                                                setbatch({
                                                  ...batch,
                                                  meetingLink: e.target.value,
                                                })
                                              }
                                              className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                            />
                                          </div>
                                        </MobileGrid>
                                      )}
                                      {(batch.location === 'Physical' ||
                                        batch.location ===
                                          'Online & Physical') && (
                                        <MobileGrid item md={4} sm={6} xs={12}>
                                          <div>
                                            <div className="mt-4">
                                              <label
                                                htmlFor="Address"
                                                className="inline-block mb-2"
                                              >
                                                Address<span className="text-red-500">*</span>
                                              </label>
                                            </div>
                                            <div className={validationErrors.address ? 'border-2 border-red-500 rounded' : ''}>
                                            
                                            <input
                                                id="address-input"
                                                type="text"
                                                placeholder="Input text"
                                                value={batch.address || ''}
                                                onChange={(e) => {
                                                  setbatch({
                                                    ...batch,
                                                    address: e.target.value,
                                                  });
                                                  clearError('address');
                                                }}
                                                className="w-full rounded border-0"
                                                style={{ outline: 'none', padding: '10px' }}
                                              />
                                            </div>
                                            {validationErrors.address && (
                                              <div className="text-red-500 text-sm mt-1">
                                                {validationErrors.address}
                                              </div>
                                            )}
                                          </div>
                                        </MobileGrid>
                                      )}

                                      <MobileGrid item xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="Description"
                                              className="inline-block mb-2"
                                            >
                                              Description
                                            </label>
                                          </div>
                                          <textarea
                                            id="Description"
                                            type="text"
                                            placeholder="Input text"
                                            value={batch.description || ''}
                                            onChange={(e) =>
                                              setbatch({
                                                ...batch,
                                                description: e.target.value,
                                              })
                                            }
                                            className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                          />
                                        </div>
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="RegistrationLink"
                                              className="inline-block mb-2"
                                            >
                                              Registration Link<span className="text-red-500">*</span>
                                            </label>
                                          </div>
                                           <div className={validationErrors.registrationLink ? 'border-2 border-red-500 rounded' : ''}>
                                          <input
                                            id="RegistrationLink"
                                            type="text"
                                            placeholder="Input text"
                                            value={batch.registrationLink || ''}
                                            onChange={(e) =>
                                              setbatch({
                                                ...batch,
                                                registrationLink:
                                                  e.target.value,
                                              })
                                            }
                                            className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                            // required
                                          />
                                          </div>
                                          {validationErrors.registrationLink && (
                                            <div className="text-red-500 text-sm mt-1">
                                              {validationErrors.registrationLink}
                                            </div>
                                          )}
                                        </div>
                                      </MobileGrid>
                                      <MobileGrid item md={4} sm={6} xs={12}>
                                        <div>
                                          <div className="mt-4">
                                            <label
                                              htmlFor="Available-calendar"
                                              className="inline-block mb-2"
                                            >
                                              Available on calendar
                                            </label>
                                          </div>
                                          <input
                                            id="Available-calendar"
                                            placeholder="Input text"
                                            checked={
                                              batch.avaialbeOnCalendar ===
                                              'true'
                                            }
                                            onChange={(e) =>
                                              setbatch({
                                                ...batch,
                                                avaialbeOnCalendar: `${e.target.checked}`,
                                              })
                                            }
                                            className="h-10 w-10"
                                            type="checkbox"
                                          />
                                        </div>
                                      </MobileGrid>
                                    </MobileGrid>
                                  </div>

                                  <div className="border rounded-[0.6rem] dark:border-darkmode-400 relative mt-7 mb-4 border-slate-200/80">
                                    <div className="absolute left-0 px-3 ml-4 -mt-2 text-xs uppercase bg-white text-slate-500">
                                      <div className="-mt-px">
                                        Course Timings
                                      </div>
                                    </div>
                                    <div className="px-2 sm:px-5 py-2 mt-4 flex flex-row flex-wrap gap-3.5">
                                      <MobileGrid
                                        container
                                        spacing={2}
                                        className="px-2 sm:px-5"
                                      >
                                        <MobileGrid item md={2} xs={12}>
                                          <div className="flex items-center h-full font-bold">
                                            When
                                          </div>
                                        </MobileGrid>
                                        <MobileGrid item md={3} sm={4.5} xs={12}>
                                          <div className={validationErrors.date ? 'border-2 border-red-500 rounded' : ''}>
                                          <input
                                            type="date"
                                            placeholder=""
                                            onChange={(e) =>
                                              setbatch({
                                                ...batch,
                                                date: e.target.value,
                                              })
                                            }
                                            // required
                                            className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                            value={batch.date || ''}
                                          />
                                          </div>
                                          {validationErrors.date && (
                                            <div className="text-red-500 text-sm mt-1">
                                              {validationErrors.date}
                                            </div>
                                          )}
                                        </MobileGrid>
                                        <MobileGrid item md={3} sm={4.5} xs={12}>
                                          <div className={validationErrors.time ? 'border-2 border-red-500 rounded' : ''}>
                                          <select
                                            value={batch.time || ''}
                                            onChange={(e) =>{
                                              setbatch({
                                                ...batch,
                                                time: e.target.value,
                                              })
                                              clearError('time');
                                            }}
                                            // required
                                            className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                          >
                                            <option value="">Time</option>
                                            {timeArray.map((value, index) => (
                                              <option value={value} key={index}>
                                                {value}
                                              </option>
                                            ))}
                                          </select>
                                           </div>
                                          {validationErrors.time && (
                                            <div className="text-red-500 text-sm mt-1">
                                              {validationErrors.time}
                                            </div>
                                          )}
                                        </MobileGrid>
                                        <MobileGrid item md={3} sm={3} xs={12}>
                                          <select
                                            value={batch.timeAt}
                                            onChange={(e) =>
                                              setbatch({
                                                ...batch,
                                                timeAt: e.target.value,
                                              })
                                            }
                                            // required
                                            className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                          >
                                            <option value="Am">AM</option>
                                            <option value="Pm">PM</option>
                                          </select>
                                        </MobileGrid>
                                      </MobileGrid>
                                      <MobileGrid
                                        container
                                        spacing={2}
                                        className="px-2 sm:px-5"
                                      >
                                        <MobileGrid item md={2} xs={12}>
                                          <div className="flex items-center h-full font-bold">
                                            Duration
                                          </div>
                                        </MobileGrid>
                                        <MobileGrid item md={3} sm={6} xs={12}>
                                          <div className="flex gap-1 items-center">
                                            <div className={`flex-1 ${validationErrors.hours ? 'border-2 border-red-500 rounded' : ''}`}>

                                            <select
                                              value={batch.hours || ''}
                                              onChange={(e) =>{
                                                setbatch({
                                                  ...batch,
                                                  hours: e.target.value,
                                                })
                                                  clearError('hours');
                                              }}
                                              // required
                                              className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                            >
                                              <option value="">Hours</option>
                                              {hoursArray.map(
                                                (value, index) => (
                                                  <option
                                                    value={value}
                                                    key={index}
                                                  >
                                                    {value}
                                                  </option>
                                                )
                                              )}
                                            </select>
                                           </div>
                                           <span className="font-bold text-sm whitespace-nowrap">
                                              hr
                                            </span>
                                          {validationErrors.hours && (
                                            <div className="text-red-500 text-sm mt-1">
                                              {validationErrors.hours}
                                            </div>
                                          )}
                                        </div>  
                                        </MobileGrid>
                                        <MobileGrid item md={3} sm={6} xs={12}>
                                          <div className="flex gap-1 items-center">
                                            <div className={`flex-1 ${validationErrors.minutes ? 'border-2  border-red-500 rounded' : ''}`}>
                                            <select
                                              value={batch.minutes || ''}
                                              onChange={(e) =>{
                                                setbatch({
                                                  ...batch,
                                                  minutes: e.target.value,
                                                })
                                                  clearError('minutes');
                                              }}
                                              // required
                                              className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                            >
                                              <option className='p-2' value="">Minutes</option>
                                              {minutesArray.map(
                                                (value, index) => (
                                                  <option
                                                    value={value}
                                                    key={index}
                                                  >
                                                    {value}
                                                  </option>
                                                )
                                              )}
                                            </select>
                                            </div>
                                            <span className="font-bold text-sm">
                                              min
                                            </span>
                                          {validationErrors.minutes && (
                                            <div className="text-red-500 text-sm mt-1">
                                              {validationErrors.minutes}
                                            </div>
                                          )}
                                         </div> 
                                        </MobileGrid>
                                      </MobileGrid>

                                      <MobileGrid
                                        container
                                        spacing={2}
                                        className="px-2 sm:px-5"
                                      >
                                        <MobileGrid item md={2} xs={0}></MobileGrid>
                                        <MobileGrid item md={8} xs={12}>
                                          <div className="flex flex-row flex-wrap items-center gap-2">
                                            <div className="flex items-center gap-2">
                                              <div className="flex items-center">
                                                <input
                                                  type="checkbox"
                                                  onChange={(e) =>
                                                    setbatch({
                                                      ...batch,
                                                      recurring:
                                                        e.target.checked,
                                                    })
                                                  }
                                                  checked={batch?.recurring}
                                                  placeholder=""
                                                />
                                              </div>
                                              <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                                <div className="text-left">
                                                  <div className="flex items-center">
                                                    <div className="font-medium">
                                                      Recurring
                                                    </div>
                                                  </div>
                                                </div>
                                              </label>
                                            </div>

                                            {batch?.recurring && (
                                              <div className="m-5 xl:mt-3 leading-relaxed flex flex-start font-bold text-md whitespace-nowrap">
                                                <span>
                                                  {batch.repeat === 'Daily'
                                                    ? `Repeat Every ${batch.repeat_after} day,`
                                                    : batch.repeat === 'Weekly'
                                                    ? `Repeat Every ${batch.repeat_after} week,`
                                                    : batch.repeat === 'Monthly'
                                                    ? `Repeat Every ${batch.repeat_after} month,`
                                                    : ''}
                                                </span>{' '}
                                                {batch.by_date && (
                                                  <span>
                                                    {' '}
                                                    {` untill ${formatDate(batch.by_date)},`}
                                                  </span>
                                                )}
                                                {batch.after_occurance && (
                                                  <span>
                                                    {' '}
                                                    {`${batch.after_occurance} occurrence(s)`}
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </MobileGrid>
                                      </MobileGrid>
                                      {batch.recurring && (
                                        <>
                                          <MobileGrid
                                            container
                                            spacing={2}
                                            className="px-2 sm:px-5"
                                          >
                                            <MobileGrid item md={2} xs={0}></MobileGrid>
                                            <MobileGrid item md={2} sm={4} xs={12}>
                                              <div className="flex items-center h-full font-bold">
                                                Recurrance
                                              </div>
                                            </MobileGrid>
                                            <MobileGrid item md={2} sm={8} xs={12}>
                                              <select
                                                placeholder=""
                                                required
                                                onChange={(e) =>
                                                  setbatch({
                                                    ...batch,
                                                    repeat: e.target.value,
                                                  })
                                                }
                                                value={batch.repeat}
                                                className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                              >
                                                <option value="Daily">
                                                  Every Day
                                                </option>
                                                <option value="Weekly">
                                                  Every Week
                                                </option>
                                                <option value="Monthly">
                                                  Every Month
                                                </option>
                                              </select>
                                            </MobileGrid>
                                          </MobileGrid>
                                          {batch.repeat === 'Weekly' && (
                                            <MobileGrid
                                              container
                                              spacing={2}
                                              className="px-2 sm:px-5"
                                            >
                                              <MobileGrid item md={2} xs={0}></MobileGrid>
                                              <MobileGrid item md={2} sm={4} xs={12}>
                                                <div className="flex items-center h-full font-bold">
                                                  Rpeat On
                                                </div>
                                              </MobileGrid>

                                              <MobileGrid item sm={8} xs={12}>
                                                <div className="flex gap-3 flex-wrap items-center">
                                                  {[
                                                    'Sun',
                                                    'Mon',
                                                    'Tue',
                                                    'Wed',
                                                    'Thu',
                                                    'Fri',
                                                    'Sat',
                                                  ].map((item, index) => (
                                                    <div key={index} className="flex gap-2 flex-wrap items-center">
                                                      <input
                                                        id={item}
                                                        value={item}
                                                        type="checkbox"
                                                        checked={isRpeatOnMatch(
                                                          item
                                                        )}
                                                        onChange={() =>
                                                          handleChangeRpeatOn(
                                                            item
                                                          )
                                                        }
                                                        name="vertical_radio_button"
                                                      />
                                                      <label
                                                        htmlFor={item}
                                                        className="cursor-pointer ml-2"
                                                      >
                                                        {item}
                                                      </label>
                                                    </div>
                                                  ))}
                                                </div>
                                              </MobileGrid>
                                            </MobileGrid>
                                          )}

                                          <MobileGrid
                                            container
                                            spacing={2}
                                            className="px-2 sm:px-5"
                                          >
                                            <MobileGrid item md={2} xs={0}></MobileGrid>
                                            <MobileGrid item md={2} sm={3} xs={12}>
                                              <div className="flex items-center h-full font-bold">
                                               Please enter the total number of occurrences or course End date.  
                                              </div>
                                            </MobileGrid>

                                            <MobileGrid item md={4} sm={6} xs={12}>
                                              <div className="flex gap-1 items-center flex-wrap">
                                                <div className="flex items-center mr-2">
                                                  <input
                                                    id="checkbox-switch-1"
                                                    type="radio"
                                                    name="end_date"
                                                    onChange={(e) =>
                                                      setbatch({
                                                        ...batch,
                                                        end_date_type:
                                                          e.target.value,
                                                      })
                                                    }
                                                    value="2"
                                                    checked={
                                                      batch.end_date_type ===
                                                      '2'
                                                    }
                                                    placeholder=""
                                                    required={
                                                      !batch.end_date_type
                                                    }
                                                  />
                                                  <label
                                                    htmlFor="checkbox-switch-1"
                                                    className="cursor-pointer ml-2"
                                                  >
                                                    After
                                                  </label>
                                                </div>
                                                <div className="relative w-auto sm:w-80 px-2 sm:px-4 flex-row flex items-center gap-2">
                                                  <div className={`w-20 h-10 overflow-hidden ${((batch.end_date_type === '2') && validationErrors.after_occurance) ? 'border-2 border-red-500 rounded' : 'border border-slate-300 rounded'}`}>
                                                    <select
                                                      onChange={(e) =>
                                                        setbatch({
                                                          ...batch,
                                                          after_occurance:
                                                            e.target.value,
                                                        })
                                                      }
                                                      value={batch.after_occurance || ''}
                                                      className="w-full h-full rounded p-2 sm:text-xs sm:p-1 border-0"
                                                    >
                                                      <option value="" disabled>Select</option>
                                                      {[...Array(60)].map((_, index) => (
                                                        <option key={index} value={index + 1}>
                                                          {index + 1}
                                                        </option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                  <span className="text-md font-bold">
                                                    occurrence<span className="text-red-500">*</span>
                                                  </span>
                                                </div>
                                                <span className="text-xl font-bold m-4">
                                                  OR
                                                </span>
                                              </div>
                                            </MobileGrid>

                                            <MobileGrid item md={4} sm={6} xs={12}>
                                              <div className="flex gap-1">
                                                <div className="flex items-center mr-2">
                                                  <input
                                                    id="checkbox-switch-2"
                                                    type="radio"
                                                    onChange={(e) =>
                                                      setbatch({
                                                        ...batch,
                                                        end_date_type:
                                                          e.target.value,
                                                      })
                                                    }
                                                    value="1"
                                                    name="end_date"
                                                    checked={
                                                      batch.end_date_type ===
                                                      '1'
                                                    }
                                                    placeholder=""
                                                    required={
                                                      !batch.end_date_type
                                                    }
                                                  />
                                                  <label
                                                    htmlFor="checkbox-switch-2"
                                                    className="cursor-pointer ml-2"
                                                  >
                                                    By
                                                  </label>
                                                </div>
                                                <div className={`px-4 w-full ${validationErrors.by_date ? 'border-2 border-red-500 rounded' : ''}`}>
                                                <div className="px-2 sm:px-4 w-full">
                                                  <input
                                                    type="date"
                                                    onChange={(e) =>{
                                                      setbatch({
                                                        ...batch,
                                                        by_date: e.target.value,
                                                      })
                                                      clearError('by_date');
                                                    }}
                                                    value={batch.by_date || ''}
                                                    className="w-full text-sm border-slate-300 rounded p-2 sm:text-xs sm:p-1"
                                                    placeholder=""
                                                    required={
                                                      batch.end_date_type ===
                                                      '1'
                                                    }
                                                  />
                                                </div>
                                                </div>
                                              </div>
                                            </MobileGrid>
                                          </MobileGrid>
                                        </>
                                      )}

                                      {(validationErrors.end_date_type || validationErrors.by_date || validationErrors.after_occurance) && (
                                        <MobileGrid container spacing={2} className="px-2 sm:px-5">
                                          <MobileGrid item md={12} xs={12}>
                                            <div className="text-red-500 text-sm mt-1">
                                              {validationErrors.end_date_type || validationErrors.by_date || validationErrors.after_occurance}
                                            </div>
                                          </MobileGrid>
                                        </MobileGrid>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="px-2 sm:px-5 py-2 mt-4 flex gap-3.5 w-full flex-col sm:flex-row items-center sm:items-end justify-end">
                                  <button
                                    type="submit"
                                    disabled={loading}
                                    className="transition duration-200 border shadow-sm items-center justify-center py-2 px-3 rounded-md font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus-visible:outline-none dark:focus:ring-slate-700 dark:focus:ring-opacity-50 [&:hover:not(:disabled)]:bg-opacity-90 [&:hover:not(:disabled)]:border-opacity-90 [&:not(button)]:text-center disabled:opacity-70 disabled:cursor-not-allowed bg-[#3b5998] border-[#3b5998] text-white dark:border-[#3b5998] w-full sm:w-32"
                                  >
                                    {loading ? 'Processing...' : 'Save'}
                                  </button>
                                  <Link
                                    to="/"
                                    className="transition duration-200 border shadow-sm items-center justify-center py-2 px-3 rounded-md font-medium cursor-pointer focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus-visible:outline-none dark:focus:ring-slate-700 dark:focus:ring-opacity-50 [&:hover:not(:disabled)]:bg-opacity-90 [&:hover:not(:disabled)]:border-opacity-90 [&:not(button)]:text-center disabled:opacity-70 disabled:cursor-not-allowed bg-[#4ab3f4] border-[#4ab3f4] text-white dark:border-[#4ab3f4] w-full sm:w-32"
                                  >
                                    Cancel
                                  </Link>
                                </div>
                              </form>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddBatchMobile;

const timeArray = [
  '1:00', '1:15', '1:30', '1:45', '2:00', '2:15', '2:30', '2:45',
  '3:00', '3:15', '3:30', '3:45', '4:00', '4:15', '4:30', '4:45',
  '5:00', '5:15', '5:30', '5:45', '6:00', '6:15', '6:30', '6:45',
  '7:00', '7:15', '7:30', '7:45', '8:00', '8:15', '8:30', '8:45',
  '9:00', '9:15', '9:30', '9:45', '10:00', '10:15', '10:30', '10:45',
  '11:00', '11:15', '11:30', '11:45', '12:00'
];

const hoursArray = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const minutesArray = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
  41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59
];